import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { uid } from 'uid';
import { PropsFromRedux } from '.';
import {
  getRequiredErrorMessage,
  sort,
  titleCase,
} from '../../../utils/common.utils';
import { CATEGORY } from '../../../interfaces/shipping.interface';
import Select from '../../form-controls/Select';
import TextInput from '../../form-controls/TextInput';
import FileUpload from '../../form-controls/FileUpload';
import { DropZoneComponent } from '../../DropzoneComponent/DropzoneComponent';
import ModalWrapper from '../../common/ModalWrapper';
import { Spinner } from '../../Spinners/Spinner';

interface Props extends PropsFromRedux {}

const schema = yup.object().shape({
  category: yup
    .object()
    .shape({
      CategoryId: yup.number(),
      CategoryName: yup.string(),
      DateCreated: yup.string(),
      DateModified: yup.string(),
      IsDeleted: yup.boolean(),
      RowVersion: yup.string(),
    })
    .required(getRequiredErrorMessage('Item category'))
    .typeError('Select a valid category'),
  item: yup.mixed().when('category', {
    is: (value: { CategoryId: number }) => !value || value?.CategoryId !== 10,
    then: yup
      .object()
      .shape({
        label: yup.string(),
        value: yup.string(),
      })
      .required(getRequiredErrorMessage('Item'))
      .typeError('Select a valid item'),
    otherwise: yup
      .string()
      .required(getRequiredErrorMessage('Item'))
      .typeError('Enter a valid item name'),
  }),
  weight: yup.mixed().when('category', {
    is: (value: { CategoryId: number }) => !value || value?.CategoryId !== 10,
    then: yup
      .object()
      .shape({
        label: yup.string(),
        value: yup.string(),
      })
      .required(getRequiredErrorMessage('Weight'))
      .typeError('Select a weight category'),
    otherwise: yup
      .number()
      .min(1, 'Value must be a number greater than 1')
      .required(getRequiredErrorMessage('Weight'))
      .typeError('Enter a valid weight in KG'),
  }),
  quantity: yup
    .number()
    .integer()
    .min(1, 'Value must be a number greater than 1')
    .required(getRequiredErrorMessage('Quantity'))
    .typeError('Quantity is required'),
  value: yup
    .number()
    .min(1, 'Value must be a number greater than 1')
    .required(getRequiredErrorMessage('Item value'))
    .typeError('Value is required'),
  item_description: yup.string().when('category', {
    is: (value: { CategoryId: number }) => !value || value?.CategoryId !== 10,
    then: yup.string(),
    otherwise: yup
      .string()
      .required(getRequiredErrorMessage('Item description'))
      .typeError('Item description is required'),
  }),
  // item_image: yup
  //   .string()
  //   .required(getRequiredErrorMessage('Item image'))
  //   .typeError('Image is required'),
});


interface Inputs {
  category: CATEGORY;
  item: { label: string; value: string } | string;
  weight: { label: string; value: string } | number;
  quantity: number;
  value: number;
  item_image: string;
  item_description: string;
}

export const ItemModal: React.FC<Props> = ({
  getpackageTypes,
  packageTypes,
  hideModal,
  modal,
}) => {
  const packageData = packageTypes?.response?.Object;
  const modalProps = modal?.ownProps;

  const categories: CATEGORY[] = packageData?.Categories || [];
  const selectedShippingItem = modalProps?.formValues;
  const { handleSubmit, control, errors, watch, setValue } = useForm<Inputs>({
    resolver: yupResolver(schema),
    defaultValues: selectedShippingItem
      ? {
          category: packageData?.Specialpackages?.find(
            (specialpackage: any) =>
              specialpackage.SpecialDomesticPackageId ===
              selectedShippingItem.SpecialPackageId
          )?.SubCategory?.Category,
          item: {
            value: selectedShippingItem?.ItemName,
            label: titleCase(selectedShippingItem?.ItemName, true),
          },
          weight: {
            value: selectedShippingItem?.WeightRange,
            label: selectedShippingItem?.WeightRange,
          },
          quantity: selectedShippingItem.Quantity,
          value: selectedShippingItem.Value,
          item_image: selectedShippingItem.ImageUrl,
        }
      : {},
  });

  const selectedCategory = watch('category');
  const selectedItem = watch('item');
  const selectedWeight = watch('weight');

  const isReqularShipment = selectedCategory?.CategoryId === 10;

  React.useEffect(() => {
    getpackageTypes();
  }, [getpackageTypes]);

  React.useEffect(() => {
    setValue('item', '');
    if (selectedCategory?.CategoryId !== 10) {
      setValue('item_name', '');
      setValue('item_weight', '');
      setValue('item_description', '');
    }
  }, [selectedCategory, setValue]);

  React.useEffect(() => {
    setValue('weight', null);
  }, [selectedItem, setValue]);

  if (packageTypes.loading) {
    return (
      <ModalWrapper hideCloseButton goBack hideModal={hideModal}>
        <div className="flex items-center justify-center">
          <Spinner color="#999" />
        </div>
      </ModalWrapper>
    );
  }

  if (packageTypes.error && !packageTypes.loading) {
    return (
      <ModalWrapper goBack hideModal={hideModal}>
        <div className="flex flex-col items-center flex-wrap justify-center">
          <p>Could not fetch Item types at the moment.</p>
          <button
            className="bg-secondary mt-4 py-2 px-6 text-white rounded"
            type="button"
            aria-label="Retry"
            onClick={() => getpackageTypes()}
          >
            Try Again
          </button>
        </div>
      </ModalWrapper>
    );
  }

  const subCategories = packageData?.DictionaryCategory[
    selectedCategory?.CategoryName
  ]?.map((category: string) => ({
    label: titleCase(category, true),
    value: category,
  }));
  const weightRange = packageData?.WeightRangeDictionaryCategory[
    (selectedItem as any)?.value
  ]?.map((weightrange: string) => ({
    label: weightrange,
    value: weightrange,
  }));

  const selectedSpecialPackage = packageData?.Specialpackages?.find(
    (specialPackage: any) =>
      specialPackage?.SubCategory?.CategoryId ===
        selectedCategory?.CategoryId &&
      specialPackage?.SubCategory?.SubCategoryName ===
        (selectedItem as any)?.value &&
      specialPackage?.SubCategory?.WeightRange ===
        (selectedWeight as any)?.value
  );

  const onSubmit = (data: Inputs) => {
    const regularShipment = data.category.CategoryId === 10;

    const reqBody = {
      ItemName: regularShipment
        ? (data.item as string)
        : (data.item as any).value,
      Description: regularShipment
        ? (data.item_description as string)
        : (data.item as any).value,
      Quantity: data.quantity,
      Value: data.value,
      WeightRange: regularShipment ? '' : (data.weight as any).value,
      ShipmentType: regularShipment ? 'Regular' : 'Special',
      SpecialPackageId: regularShipment
        ? 0
        : selectedSpecialPackage?.SpecialDomesticPackageId,
      ShipmentItemId: uid(8),
      ItemType: selectedSpecialPackage?.SpecialDomesticPackageType || '',
      Weight: regularShipment ? (data.weight as number) : 0,
      ImageUrl: data.item_image,
    };
    modalProps?.formValues
      ? modalProps?.formAction(modalProps?.formValues?.ShipmentItemId, reqBody)
      : modalProps?.formAction(reqBody);
  };

  return (
    <ModalWrapper disablePadding hideModal={hideModal}>
      <form onSubmit={handleSubmit(onSubmit)} className="w-full">
        <div className="">
          <h4 className="p-5 font-semibold rounded-t-md  border-b border-gray-300 text-center">
            Item's Info
          </h4>
          <div className="w-full mx-auto p-8">
            <div className="mb-8 w-full">
              <Controller
                name="category"
                control={control}
                defaultValue=""
                render={({ name, value, onChange }) => (
                  <Select
                    name={name}
                    value={value}
                    id={name}
                    valueKey="CategoryId"
                    labelKey="CategoryName"
                    wrapperClassName="w-full"
                    error={errors['category']}
                    placeholder="Category"
                    labelClassName="mb-2 ml-2 flex"
                    label="Select category"
                    customChange={onChange}
                    className="rounded-lg"
                    noOptionsMessage={() =>
                      packageTypes?.loading
                        ? 'Loading...'
                        : 'No categories found'
                    }
                    options={sort(categories, 'CategoryName') || []}
                  />
                )}
              />
            </div>
            {isReqularShipment ? (
              <>
                <div className="mb-8 w-full">
                  <Controller
                    name="item"
                    control={control}
                    defaultValue=""
                    render={({ name, value, onChange }) => (
                      <TextInput
                        name={name}
                        value={value}
                        className="border-gray-300 rounded-lg border p-3 w-full"
                        id={name}
                        labelClassName="mb-2 ml-2 flex"
                        label="Item name"
                        type="text"
                        wrapperClassName="w-full"
                        error={errors['item']}
                        placeholder="Enter item name"
                        customChange={onChange}
                      />
                    )}
                  />
                </div>
                <div className="mb-8 w-full">
                  <Controller
                    name="item_description"
                    control={control}
                    defaultValue=""
                    render={({ name, value, onChange }) => (
                      <TextInput
                        name={name}
                        value={value}
                        className="border-gray-300 rounded-lg border p-3 w-full"
                        id={name}
                        labelClassName="mb-2 ml-2 flex"
                        label="Item description"
                        type="text"
                        wrapperClassName="w-full"
                        error={errors['item_description']}
                        placeholder="Enter item description"
                        customChange={onChange}
                      />
                    )}
                  />
                </div>
                <div className="mb-8 w-full">
                  <Controller
                    name="weight"
                    control={control}
                    defaultValue=""
                    render={({ name, value, onChange }) => (
                      <TextInput
                        name={name}
                        value={value}
                        className="border-gray-300 rounded-lg border p-3 w-full"
                        id={name}
                        labelClassName="mb-2 ml-2 flex"
                        label="Item Weight"
                        type="number"
                        wrapperClassName="w-full"
                        error={errors['weight']}
                        placeholder="Enter item weight in KG"
                        customChange={onChange}
                      />
                    )}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="mb-8 w-full">
                  <Controller
                    name="item"
                    control={control}
                    defaultValue=""
                    render={({ name, value, onChange }) => (
                      <Select
                        name={name}
                        value={value}
                        id={name}
                        wrapperClassName="w-full"
                        error={errors['item']}
                        placeholder="Item"
                        labelClassName="mb-2 ml-2 flex"
                        label="Select item"
                        customChange={onChange}
                        noOptionsMessage={() =>
                          packageTypes?.loading
                            ? 'Loading...'
                            : 'No subcategories found'
                        }
                        className="rounded-lg"
                        options={sort(subCategories, 'value') || []}
                      />
                    )}
                  />
                </div>
                <div className="mb-8 w-full">
                  <Controller
                    name="weight"
                    control={control}
                    defaultValue=""
                    render={({ name, value, onChange }) => (
                      <Select
                        name={name}
                        value={value}
                        id={name}
                        wrapperClassName="w-full"
                        error={errors['weight']}
                        placeholder="Weight"
                        labelClassName="mb-2 ml-2 flex"
                        label="Select weight range (KG)"
                        customChange={onChange}
                        className="rounded-lg"
                        noOptionsMessage={() =>
                          packageTypes?.loading
                            ? 'Loading...'
                            : 'No weight ranges found'
                        }
                        options={weightRange || []}
                      />
                    )}
                  />
                </div>
              </>
            )}

            <div className="flex flex-wrap lg:flex-nowrap w-full lg:gap-4">
              <div className="mb-8 w-full lg:w-1/2">
                <Controller
                  name="quantity"
                  control={control}
                  defaultValue=""
                  render={({ name, value, onChange }) => (
                    <TextInput
                      name={name}
                      value={value}
                      id={name}
                      labelClassName="mb-2 ml-2 flex"
                      className="border-gray-300 rounded-lg border p-3 w-full"
                      label="Quantity"
                      type="number"
                      wrapperClassName="w-full"
                      error={errors['quantity']}
                      placeholder="Quantity"
                      customChange={onChange}
                    />
                  )}
                />
              </div>
              <div className="mb-8 w-full lg:w-1/2">
                <Controller
                  name="value"
                  control={control}
                  defaultValue=""
                  render={({ name, value, onChange }) => (
                    <TextInput
                      name={name}
                      value={value}
                      id={name}
                      type="number"
                      className="border-gray-300 rounded-lg border p-3 w-full"
                      labelClassName="mb-2 ml-2 flex"
                      label="Value (Naira)"
                      helperText="This information is important because it insures your shipment against theft and in-transit damages"
                      wrapperClassName="w-full"
                      error={errors['value']}
                      placeholder="Value"
                      customChange={onChange}
                    />
                  )}
                />
              </div>
            </div>

            <div className="w-full flex">
              <Controller
                name="item_image"
                control={control}
                defaultValue=""
                render={({ name, value, onChange }) => (
                  <FileUpload
                    wrapperClassName="w-full"
                    labelClassName="mb-2 ml-1 flex"
                    label="Upload item"
                    name={name}
                    id={name}
                    value={value}
                    maxFiles={1}
                    fileProps={
                      selectedShippingItem?.ImageUrl
                        ? [selectedShippingItem?.ImageUrl]
                        : []
                    }
                    customChange={(files: string[]) => {
                      onChange(files[0] || '');
                    }}
                    // error={errors['item_image']}
                    accept="image/*"
                    DropZoneComponent={DropZoneComponent}
                  />
                )}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap justify-around p-4">
          <button
            type="button"
            onClick={() => hideModal()}
            className="border border-secondary text-secondary w-full sm:w-auto py-3 px-14 font-bold text-lg rounded"
          >
            Back
          </button>
          <button
            type="submit"
            className="border border-secondary bg-secondary w-full sm:w-auto text-white py-3 px-14 font-bold text-lg rounded"
          >
            Finish
          </button>
        </div>
      </form>
    </ModalWrapper>
  );
};
